<template>
  <div class="dir-picker">
    <p v-if="hint" class="dir-hint dir-picker-hint">{{ hint }}</p>

    <!-- Directive search over the curated catalog. -->
    <div class="dir-field">
      <label>Search directives</label>
      <div class="dir-search">
        <span class="glyphicon glyphicon-search bi bi-search"></span>
        <input
          v-model.trim="searchQuery"
          type="text"
          class="dir-input dir-search-input"
          placeholder="Search by name, description or SNOMED code…"
          data-cy="picker-search"
        />
        <button
          v-if="searchQuery"
          class="dir-search-clear"
          @click="searchQuery = ''"
          aria-label="Clear search"
        >
          <span class="glyphicon glyphicon-remove bi bi-x-lg"></span>
        </button>
      </div>
      <small v-if="searchLoading" class="dir-hint">Searching…</small>
    </div>

    <!-- Unified results: the curated common directives always rank first, then
         the rest of the SNOMED release. One list, one count — the operator
         never has to opt in to a second search. -->
    <template v-if="searchQuery">
      <p class="dir-cat-label">
        Results
        <span v-if="!resultsLoading">({{ unifiedResults.length }})</span>
      </p>
      <small v-if="resultsLoading" class="dir-hint">Searching…</small>
      <p v-else-if="unifiedResults.length === 0" class="dir-hint">
        No directives match “{{ searchQuery }}”.
      </p>
      <p v-if="!snomedLoading && !snomedAvailable" class="dir-hint dir-snomed-unavailable">
        <span class="glyphicon glyphicon-warning-sign bi bi-exclamation-triangle"></span>
        Full-SNOMED search is unavailable right now — common directives still match.
      </p>
      <div v-if="unifiedResults.length" class="dir-card-list">
        <div
          v-for="item in unifiedResults"
          :key="item.key"
          class="dir-card"
          :class="{ active: item.selected, 'dir-card-snomed': item.kind === 'snomed' }"
        >
          <button
            type="button"
            class="dir-card-select"
            :data-cy="item.kind === 'snomed' ? 'snomed-choice' : 'catalog-choice'"
            :data-catalog-id="item.kind === 'catalog' ? item.choice.id : null"
            :data-snomed="item.code"
            :aria-pressed="item.selected ? 'true' : 'false'"
            @click="toggleResult(item)"
          >
            <span class="glyphicon dir-card-check" :class="item.selected ? 'glyphicon-check bi bi-check-square-fill' : 'glyphicon-unchecked bi bi-square'"></span>
            <span class="dir-card-body">
              <span class="dir-card-name">
                {{ item.label }}
                <span v-if="item.kind === 'catalog'" class="dir-card-tag">Common</span>
              </span>
              <span class="dir-card-desc" :class="{ 'dir-code-mono': item.kind === 'snomed' }">
                {{ item.description }}
              </span>
            </span>
          </button>
          <div v-if="item.kind === 'catalog'" class="dir-code-info">
            <button
              type="button"
              class="dir-code-info-btn"
              :aria-label="'Show SNOMED code for ' + item.label"
              :aria-expanded="openCodeInfoFor === item.choice.id ? 'true' : 'false'"
              @click.stop="toggleCodeInfo(item.choice.id)"
              @mouseenter="hoverCodeInfo(item.choice.id)"
              @mouseleave="unhoverCodeInfo"
              @focus="hoverCodeInfo(item.choice.id)"
              @blur="unhoverCodeInfo"
            >
              <span class="glyphicon glyphicon-info-sign bi bi-info-circle"></span>
            </button>
            <div v-if="openCodeInfoFor === item.choice.id" class="dir-code-popover" role="dialog">
              <div class="dir-code-popover-row">
                <span class="dir-code-popover-label">{{ codeSystemLabel(item.code_system) }}</span>
                <span class="dir-code-mono">{{ item.code }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>

    <!-- Curated catalog, grouped by category (the common ones). -->
    <template v-else>
      <div v-for="cat in catalogByCategory" :key="cat.code" class="dir-cat-group">
        <div class="dir-cat-label">{{ cat.label }}</div>
        <div class="dir-card-list">
          <div
            v-for="choice in cat.choices"
            :key="choice.id"
            class="dir-card"
            :class="{ active: selectedCatalogIds.includes(choice.id) }"
          >
            <button
              type="button"
              class="dir-card-select"
              data-cy="catalog-choice"
              :data-catalog-id="choice.id"
              :data-snomed="choice.code"
              :aria-pressed="selectedCatalogIds.includes(choice.id) ? 'true' : 'false'"
              @click="toggleChoice(cat.code, choice.id)"
            >
              <span class="glyphicon dir-card-check" :class="selectedCatalogIds.includes(choice.id) ? 'glyphicon-check bi bi-check-square-fill' : 'glyphicon-unchecked bi bi-square'"></span>
              <span class="dir-card-body">
                <span class="dir-card-name">{{ choice.full_name || choice.choice_label }}</span>
                <span class="dir-card-desc">{{ choice.description || 'No description available.' }}</span>
              </span>
            </button>
            <div class="dir-code-info">
              <button
                type="button"
                class="dir-code-info-btn"
                :aria-label="'Show SNOMED code for ' + (choice.full_name || choice.choice_label)"
                :aria-expanded="openCodeInfoFor === choice.id ? 'true' : 'false'"
                @click.stop="toggleCodeInfo(choice.id)"
                @mouseenter="hoverCodeInfo(choice.id)"
                @mouseleave="unhoverCodeInfo"
                @focus="hoverCodeInfo(choice.id)"
                @blur="unhoverCodeInfo"
              >
                <span class="glyphicon glyphicon-info-sign bi bi-info-circle"></span>
              </button>
              <div v-if="openCodeInfoFor === choice.id" class="dir-code-popover" role="dialog">
                <div class="dir-code-popover-row">
                  <span class="dir-code-popover-label">{{ codeSystemLabel(choice.code_system) }}</span>
                  <span class="dir-code-mono">{{ choice.code }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>

    <!-- Selected summary (kept visible while searching so picks aren't lost).
         Includes both curated-catalog picks and full-SNOMED picks. -->
    <div v-if="!hideSummary && selectedSummary.length" class="dir-selected-box">
      <span class="dir-selected-label">Selected ({{ selectedSummary.length }}):</span>
      <span v-for="c in selectedSummary" :key="c.key" class="dir-pill-selected">
        {{ c.label }}
        <span class="glyphicon glyphicon-remove bi bi-x-lg" @click.stop="removeSummary(c)"></span>
      </span>
    </div>

    <!-- Soft cross-category conflict warning (never a hard block). -->
    <div v-if="conflictWarning" class="dir-soft-warning">
      <span class="glyphicon glyphicon-warning-sign bi bi-exclamation-triangle"></span>
      {{ conflictWarning }}
    </div>
  </div>
</template>

<script setup>
// Shared directive picker: catalog load + search + multi-select with
// within-category mutual exclusivity and a soft cross-category conflict warning.
// Used by both AddDirectivesModal (manual entry) and the sign-off "assign codes"
// flow, so the two paths are identical. Emits `update:selected` with the chosen
// directive objects ({ catalog_id, code, label }).
import { ref, computed, watch, onMounted, onBeforeUnmount } from "vue";
import { CONFLICT_PAIRS } from "@/utils/directiveConflicts";
import {
  getDirectiveCatalog,
  getDirectiveCatalogMock,
  searchDirectives,
  searchDirectivesMock,
  searchSnomed,
  codeKey,
  codeSystemLabel,
} from "@/api/directivesApi";

const USE_MOCK_DATA = false;
const USE_MOCK_CATALOG = false; // live fetch falls back to the mock on error

defineProps({
  hint: {
    type: String,
    default:
      "Choose one or more directives. Within a category, choices are mutually " +
      "exclusive. (At least one is required.) Each shows its SNOMED code, full " +
      "name and description — verify you are selecting the right one.",
  },
  // Suppress the inline "Selected (N)" box when the host renders its own
  // selection summary (e.g. the Add modal's sticky sidebar).
  hideSummary: { type: Boolean, default: false },
});
const emit = defineEmits(["update:selected"]);

const catalog = ref([]);
const selectedCatalogIds = ref([]);
const searchQuery = ref("");
const searchResults = ref([]);
const searchLoading = ref(false);
let searchToken = 0;
let searchTimer = null;

// Full-SNOMED search: always runs alongside the catalog search (the backend
// enforces a >=3-char floor). Picks have no catalog_id — tracked separately as
// { code, label }.
const snomedResults = ref([]);
const snomedLoading = ref(false);
const snomedAvailable = ref(true); // false when the reference table isn't built
const selectedSnomed = ref([]);
let snomedToken = 0;
let snomedTimer = null;

async function loadCatalog() {
  if (USE_MOCK_CATALOG) {
    catalog.value = getDirectiveCatalogMock();
    return;
  }
  try {
    const rows = await getDirectiveCatalog();
    catalog.value = Array.isArray(rows) && rows.length ? rows : getDirectiveCatalogMock();
  } catch (e) {
    console.error("[directives] catalog load failed; falling back to mock", e);
    catalog.value = getDirectiveCatalogMock();
  }
}
onMounted(loadCatalog);

// ---- catalog grouping (preserves seed sort order) ----
const catalogByCategory = computed(() => {
  const sorted = catalog.value.slice().sort((a, b) => a.sort_order - b.sort_order);
  const groups = [];
  const byCode = {};
  for (const row of sorted) {
    if (!byCode[row.category_code]) {
      byCode[row.category_code] = { code: row.category_code, label: row.category_label, choices: [] };
      groups.push(byCode[row.category_code]);
    }
    byCode[row.category_code].choices.push(row);
  }
  return groups;
});

// All selected catalog rows (summary stays visible even while a search filters the list).
const selectedChoices = computed(() =>
  catalog.value.filter((c) => selectedCatalogIds.value.includes(c.id))
);

// Unified selection summary across catalog + SNOMED picks (for the inline pills).
const selectedSummary = computed(() => {
  const cat = selectedChoices.value.map((c) => ({
    key: "c" + c.id,
    label: c.full_name || c.choice_label,
    kind: "catalog",
    catId: c.id,
    catCode: c.category_code,
  }));
  const sno = selectedSnomed.value.map((r) => ({
    key: "s" + r.code,
    label: r.label,
    kind: "snomed",
    snomed: r,
  }));
  return cat.concat(sno);
});
function removeSummary(item) {
  if (item.kind === "snomed") {
    toggleSnomed(item.snomed);
  } else {
    toggleChoice(item.catCode, item.catId);
  }
}

// Single results list: curated catalog matches first (they are the common
// directives), then everything else in the SNOMED release. A SNOMED row whose
// concept is already covered by a catalog match is dropped so nothing appears twice.
const unifiedResults = computed(() => {
  const catalogItems = searchResults.value
    .slice()
    .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
    .map((c) => ({
      key: "c" + c.id,
      kind: "catalog",
      choice: c,
      code: c.code,
      code_system: c.code_system || "SNOMED",
      label: c.full_name || c.choice_label,
      description: c.description || "No description available.",
      selected: selectedCatalogIds.value.includes(c.id),
    }));
  // Only SNOMED-coded catalog rows can shadow a SNOMED search hit; a local code
  // never refers to the same concept even if the strings happen to match.
  const covered = new Set(
    catalogItems.map((i) => codeKey(i.code_system, i.code))
  );
  const snomedItems = snomedResults.value
    .filter((r) => !covered.has(codeKey("SNOMED", r.code)))
    .map((r) => ({
      key: "s" + r.code,
      kind: "snomed",
      row: r,
      code: r.code,
      code_system: "SNOMED",
      label: r.label,
      description: "SNOMED CT " + r.code,
      selected: isSnomedSelected(r.code),
    }));
  return catalogItems.concat(snomedItems);
});

// "Searching…" stays up until both sources have answered, so the count never
// jumps as the second response lands.
const resultsLoading = computed(() => searchLoading.value || snomedLoading.value);

function toggleResult(item) {
  if (item.kind === "snomed") {
    toggleSnomed(item.row);
  } else {
    toggleChoice(item.choice.category_code, item.choice.id);
  }
}

// ---- directive search (debounced; stale responses ignored) ----
// The pending timer is cleared on every keystroke: the token guard alone would
// discard stale *responses* but still fire one request per character, which
// matters now that each keystroke also hits the full-SNOMED FULLTEXT search.
watch(searchQuery, (q) => {
  const query = (q || "").trim();
  if (!query) {
    clearTimeout(searchTimer);
    clearTimeout(snomedTimer);
    searchResults.value = [];
    searchLoading.value = false;
    snomedResults.value = [];
    snomedAvailable.value = true;
    snomedLoading.value = false;
    return;
  }
  searchLoading.value = true;
  const token = ++searchToken;
  clearTimeout(searchTimer);
  searchTimer = setTimeout(async () => {
    try {
      const rows = USE_MOCK_DATA ? searchDirectivesMock(query) : await searchDirectives(query);
      if (token !== searchToken) return;
      searchResults.value = Array.isArray(rows) ? rows : [];
    } catch (e) {
      if (token !== searchToken) return;
      console.error("[directives] search failed", e);
      searchResults.value = [];
    } finally {
      if (token === searchToken) searchLoading.value = false;
    }
  }, 200);
  // SNOMED is searched on every keystroke too — no opt-in step.
  runSnomedSearch();
});

// ---- full-SNOMED search (debounced; stale responses ignored) ----
function runSnomedSearch() {
  const query = (searchQuery.value || "").trim();
  if (query.length < 3) {
    clearTimeout(snomedTimer);
    snomedResults.value = [];
    snomedAvailable.value = true;
    snomedLoading.value = false;
    return;
  }
  snomedLoading.value = true;
  const token = ++snomedToken;
  clearTimeout(snomedTimer);
  snomedTimer = setTimeout(async () => {
    try {
      const { results, available } = await searchSnomed(query);
      if (token !== snomedToken) return;
      snomedResults.value = Array.isArray(results) ? results : [];
      snomedAvailable.value = available !== false;
    } catch (e) {
      if (token !== snomedToken) return;
      console.error("[directives] SNOMED search failed", e);
      snomedResults.value = [];
      snomedAvailable.value = false; // couldn't search — treat as unavailable
    } finally {
      if (token === snomedToken) snomedLoading.value = false;
    }
  }, 200);
}
function isSnomedSelected(code) {
  return selectedSnomed.value.some((r) => String(r.code) === String(code));
}
function toggleSnomed(r) {
  const i = selectedSnomed.value.findIndex((x) => String(x.code) === String(r.code));
  if (i >= 0) {
    selectedSnomed.value.splice(i, 1);
  } else {
    selectedSnomed.value.push({ code: r.code, label: r.label });
  }
}

function toggleChoice(categoryCode, choiceId) {
  const idx = selectedCatalogIds.value.indexOf(choiceId);
  if (idx >= 0) {
    selectedCatalogIds.value.splice(idx, 1);
    return;
  }
  // Within-category mutual exclusivity: clear any other choice in this category.
  const idsInCat = catalog.value
    .filter((c) => c.category_code === categoryCode)
    .map((c) => c.id);
  selectedCatalogIds.value = selectedCatalogIds.value.filter((id) => !idsInCat.includes(id));
  selectedCatalogIds.value.push(choiceId);
}

// Soft cross-category conflict warning (e.g. Full Code + DNR). Never blocks.
// Pairs live in @/utils/directiveConflicts (shared with the detail page).
const conflictWarning = computed(() => {
  const selectedCats = catalog.value
    .filter((c) => selectedCatalogIds.value.includes(c.id))
    .map((c) => c.category_code);
  for (const [a, b] of CONFLICT_PAIRS) {
    if (selectedCats.includes(a) && selectedCats.includes(b)) {
      return "These selections may be clinically contradictory. The signing clinician will review — you can still continue.";
    }
  }
  return "";
});

function selectedDirectives() {
  const fromCatalog = catalog.value
    .filter((c) => selectedCatalogIds.value.includes(c.id))
    .map((c) => ({
      catalog_id: c.id,
      code: c.code,
      // Carried through from the catalog row: several categories have no SNOMED
      // concept and are coded ERRIFF-LOCAL (see issue #177).
      code_system: c.code_system || "SNOMED",
      label: c.choice_label,
    }));
  // Full-SNOMED picks have no catalog row — catalog_id is null (the backend
  // and directive.catalog_id already support this). Always SNOMED by origin.
  const fromSnomed = selectedSnomed.value.map((r) => ({
    catalog_id: null,
    code: r.code,
    code_system: "SNOMED",
    label: r.label,
  }));
  return fromCatalog.concat(fromSnomed);
}

// Emit the resolved directive objects whenever either selection changes.
watch(
  [selectedCatalogIds, selectedSnomed],
  () => emit("update:selected", selectedDirectives()),
  { deep: true }
);

// ---- code-info popover (SNOMED code; mirrors the Coded-directives card) ----
const openCodeInfoFor = ref(null);
const codeInfoPinned = ref(false);
function toggleCodeInfo(id) {
  if (openCodeInfoFor.value === id && codeInfoPinned.value) {
    closeCodeInfo();
  } else {
    openCodeInfoFor.value = id;
    codeInfoPinned.value = true;
  }
}
function hoverCodeInfo(id) {
  if (!codeInfoPinned.value) openCodeInfoFor.value = id;
}
function unhoverCodeInfo() {
  if (!codeInfoPinned.value) openCodeInfoFor.value = null;
}
function closeCodeInfo() {
  openCodeInfoFor.value = null;
  codeInfoPinned.value = false;
}
function onCodeInfoKeydown(e) {
  if (e.key === "Escape" && openCodeInfoFor.value !== null) closeCodeInfo();
}
function onDocClickForCodeInfo(e) {
  if (!codeInfoPinned.value) return;
  if (e.target.closest && e.target.closest(".dir-code-info")) return;
  closeCodeInfo();
}
onMounted(() => {
  document.addEventListener("keydown", onCodeInfoKeydown);
  document.addEventListener("click", onDocClickForCodeInfo, true);
});
onBeforeUnmount(() => {
  document.removeEventListener("keydown", onCodeInfoKeydown);
  document.removeEventListener("click", onDocClickForCodeInfo, true);
  clearTimeout(searchTimer);
  clearTimeout(snomedTimer);
});

function reset() {
  clearTimeout(searchTimer);
  clearTimeout(snomedTimer);
  selectedCatalogIds.value = [];
  selectedSnomed.value = [];
  searchQuery.value = "";
  searchResults.value = [];
  searchLoading.value = false;
  snomedResults.value = [];
  snomedAvailable.value = true;
  snomedLoading.value = false;
  closeCodeInfo();
}
// Remove one selection by catalog id (for host-rendered summaries).
function deselect(catalogId) {
  const idx = selectedCatalogIds.value.indexOf(catalogId);
  if (idx >= 0) selectedCatalogIds.value.splice(idx, 1);
}
defineExpose({ reset, deselect });
</script>

<style scoped>
.dir-picker {
  min-width: 0;
}
.dir-picker-hint {
  margin-bottom: 4px;
}
.dir-field {
  margin-top: 16px;
}
.dir-field label {
  display: block;
  font-weight: 600;
  font-size: 14px;
  color: var(--eh-teal-ink);
  margin-bottom: 6px;
}
.dir-input {
  width: 100%;
  padding: 9px 12px;
  border-radius: 8px;
  font-size: 14px;
}
.dir-hint {
  display: block;
  margin-top: 6px;
  font-size: 12px;
  color: var(--eh-muted);
}
.dir-cat-group {
  margin-bottom: 14px;
}
.dir-cat-label {
  font-weight: 600;
  font-size: 13px;
  color: var(--eh-muted);
  margin-bottom: 6px;
}
.dir-soft-warning {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-top: 14px;
  padding: 12px 14px;
  background: color-mix(in srgb, var(--eh-amber) 18%, var(--eh-white));
  border: 1px solid var(--eh-amber);
  border-radius: 8px;
  color: var(--eh-teal-ink);
  font-size: 13px;
}
.dir-soft-warning .glyphicon {
  margin-top: 1px;
}

/* Directive search */
.dir-search {
  position: relative;
}
.dir-search > .glyphicon {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--eh-muted-border);
  font-size: 14px;
}
.dir-search-input {
  padding-left: 36px;
  padding-right: 36px;
}
.dir-search-clear {
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  border: none;
  background: transparent;
  color: var(--eh-muted-border);
  cursor: pointer;
  font-size: 13px;
}

/* Rich directive cards (code + full name + description) */
.dir-card-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.dir-card {
  display: flex;
  align-items: stretch;
  border: 1px solid var(--eh-divider);
  border-radius: 8px;
  background: var(--eh-white);
  transition: all 0.15s;
}
.dir-card:hover {
  border-color: var(--eh-teal);
}
.dir-card.active {
  border-color: var(--eh-teal);
  background: var(--eh-teal-wash);
}
/* Clickable select region (the card minus the info button) */
.dir-card-select {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  flex: 1 1 auto;
  min-width: 0;
  text-align: left;
  padding: 12px 14px;
  border: none;
  background: transparent;
  cursor: pointer;
  font-family: inherit;
}
.dir-card-check {
  margin-top: 2px;
  font-size: 15px;
  color: var(--eh-teal);
  flex-shrink: 0;
}
.dir-card-body {
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
}
.dir-card-name {
  color: var(--eh-teal-ink);
  font-size: 14px;
  font-weight: 600;
}
.dir-card-desc {
  font-size: 12.5px;
  color: var(--eh-muted);
  line-height: 1.4;
}
/* Marks a curated (common) directive inside the unified results list. */
.dir-card-tag {
  display: inline-block;
  margin-left: 8px;
  padding: 1px 7px;
  border-radius: 999px;
  background: var(--eh-teal-wash);
  color: var(--eh-teal);
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.3px;
  text-transform: uppercase;
  vertical-align: middle;
}
/* Code-info affordance (right side; mirrors the Coded-directives card) */
.dir-code-info {
  position: relative;
  flex: none;
  padding: 10px 10px 0 0;
}
.dir-code-info-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: 1px solid var(--eh-divider);
  border-radius: 8px;
  background: var(--eh-white);
  color: var(--eh-teal);
  font-size: 14px;
  line-height: 1;
  cursor: pointer;
}
.dir-code-info-btn:hover,
.dir-code-info-btn:focus-visible {
  background: var(--eh-teal-wash);
  border-color: var(--eh-teal);
  outline: none;
}
.dir-code-mono {
  font-family: var(--eh-font-data);
  font-size: 12px;
  font-weight: 700;
  color: var(--eh-teal);
}
.dir-code-popover {
  position: absolute;
  top: calc(100% + 6px);
  right: 0;
  z-index: 50;
  min-width: 180px;
  max-width: 240px;
  padding: 10px 12px;
  background: var(--eh-teal-ink);
  color: var(--eh-white);
  border-radius: 8px;
  box-shadow: 0 10px 25px -8px rgba(0, 0, 0, 0.35);
  font-size: 12.5px;
}
.dir-code-popover-row {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  padding: 2px 0;
}
.dir-code-popover-label {
  color: var(--eh-muted-border);
  font-weight: 600;
}
.dir-code-popover .dir-code-mono {
  color: var(--eh-teal);
}

/* Full-SNOMED results */
/* Dashed border signals a long-tail SNOMED concept (coded, but not part of the
   curated catalog); solid once selected. */
.dir-card-snomed {
  border-style: dashed;
}
.dir-card-snomed.active {
  border-style: solid;
}
.dir-snomed-unavailable {
  display: flex;
  align-items: center;
  gap: 6px;
  color: var(--eh-teal-ink);
}

/* Selected summary */
.dir-selected-box {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 14px;
  padding: 12px 14px;
  background: var(--eh-muted-fill);
  border: 1px solid var(--eh-divider);
  border-radius: 8px;
}
.dir-selected-label {
  font-size: 13px;
  font-weight: 600;
  color: var(--eh-muted);
}
.dir-pill-selected {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 600;
  background: var(--eh-teal-wash);
  color: var(--eh-teal);
  border: 1px solid var(--eh-teal);
  padding: 3px 8px;
  border-radius: 999px;
}
.dir-pill-selected .glyphicon {
  cursor: pointer;
  font-size: 10px;
}

/* ===== Phone layout. Must stay LAST in this block: these rules share their
   specificity with the base rules above, so anything declared after them would
   silently win. ===== */
@media (max-width: 600px) {
  /* Controls a finger has to hit: the info affordance and the search field were
     28px and 41px, both under the 44px minimum. */
  .dir-code-info-btn {
    width: 44px;
    height: 44px;
    margin: -8px -8px -8px 0;
  }
  .dir-search-input,
  .dir-input {
    min-height: 44px;
  }
  .dir-search-clear {
    width: 44px;
    height: 44px;
  }
  .dir-card {
    padding: 12px;
    gap: 10px;
  }
  /* The popover is anchored to a button near the right edge; at 390px a fixed
     width pushed it past the viewport. */
  .dir-code-popover {
    right: 0;
    left: auto;
    max-width: calc(100vw - 48px);
  }
}
</style>
